import {
  useEffect, useState, ReactNode, useCallback,
} from 'react';
import styled from 'styled-components';

import {
  TextInput, PrimaryButton, MSButton, MicrosoftIcon, LoginFormTitle, LoginLinkStyled, LoginErrorText, LoginColumnWrapper,
  WarningInfo,
} from '..';
import { Flex, FlexColumn } from '../Flex';
import {
  useCallAzureAuth,
  useAuth,
  getSessionTimeout,
  useIsMsalAuth,
  useSessionContext,
} from '../../authentication';
import { useEnterKeyEvent } from '../../util';
import { GeneralLoginPage } from './GeneralLoginPage';

const ShowHideWrapper = styled(Flex)`${({ theme }) => `
  cursor: default;
  align-items: center;
  padding-right: ${theme.borderWidth[2]};
`}`;

const ShowHideIcon = styled.i`${({ theme }) => `
  font-size: ${theme.fontSize[3]};
  margin-right: ${theme.padding[1]};
  color: ${theme.colours.neutral[3]};
`}`;

const ShowHideLabel = styled.span`${({ theme }) => `
  font-family: 'Open Sans';
  font-size: ${theme.fontSize[1]};
  line-height: ${theme.fontSize[3]};
  color: ${theme.colours.neutral[3]};
`}`;

const Divider = styled.div`${({ theme }) => `
  width: 100%;
  height: ${theme.borderWidth[1]};
  background-color: ${theme.colours.neutral[6]};
`}`;

const DividerContainer = styled(Flex)`${({ theme }) => `
  gap: calc(${theme.borderWidth[2]} + ${theme.borderWidth[3]});
  justify-content: center;
  align-items: center;
`}`;

const TextDivider = styled.div`${({ theme }) => `
  color:${theme.colours.neutral[2]};
  font-family: 'Open Sans';
  font-size: ${theme.fontSize[2]};
  font-style: normal;
  font-weight: ${theme.fontWeight.normal};
  line-height: ${theme.fontSize[6]};
`}`;

export function LoginForm({ children, onAuth }: { children?: ReactNode, onAuth: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(false);
  const { login } = useAuth();
  const { validate } = useAuth();
  const isInternalUser = useIsMsalAuth();
  const callAzureAuth = useCallAzureAuth();
  const [isShowPass, setIsShowPass] = useState<boolean>(false);
  const { getIsLoggedOut } = useSessionContext();

  useEffect(() => {
    const checkAuth = async () => {
      if (isInternalUser) {
        onAuth();
      } else {
        // Using isLoggedOut localstorage flag as a cache.
        // This will reduce unnecessary calls to validate endpoint.
        if (getIsLoggedOut()) return;
        const isExternalUser = await validate();
        if (isExternalUser) {
          onAuth();
        }
      }
    };
    checkAuth();
  }, [isInternalUser, onAuth]);

  const onClicklogin = () => {
    login(email, password)
      .then(() => onAuth())
      .catch(() => setLoginError(true));
  };

  useEnterKeyEvent(onClicklogin);

  const onClickUnderwriterlogin = async () => {
    await callAzureAuth();
  };

  const renderShowHidePass = useCallback((isShow: boolean) => (
    <ShowHideWrapper>
      <ShowHideIcon className={isShow ? 'mi-eye' : 'mi-eye-off'} />
      <ShowHideLabel>{isShow ? 'Hide' : 'Show'}</ShowHideLabel>
    </ShowHideWrapper>
  ), [isShowPass]);

  return (
    <GeneralLoginPage>
      <LoginFormTitle>Broker portal</LoginFormTitle>

      {getSessionTimeout() && (
        <WarningInfo>
          Your session has expired due to inactivity.
        </WarningInfo>
      )}

      <TextInput
        id="email"
        label="Email address"
        required
        value={email}
        onChange={setEmail}
        touchOverride={loginError}
        hideErrorMessage
        errors={loginError ? ['error'] : []}
      />

      <LoginColumnWrapper>
        <FlexColumn>
          <TextInput
            required
            id="password"
            label="Password"
            value={password}
            onChange={setPassword}
            type={isShowPass ? 'text' : 'password'}
            suffix={renderShowHidePass(isShowPass)}
            onClickSuffix={() => setIsShowPass(!isShowPass)}
            touchOverride={loginError}
            errors={loginError ? ['error'] : []}
            hideErrorMessage
          />
          {loginError && (
            <LoginErrorText>Invalid credentials. Please make sure your email address and password are correct.</LoginErrorText>
          )}
        </FlexColumn>
        <LoginLinkStyled to="/forgot-password">Forgot password?</LoginLinkStyled>
      </LoginColumnWrapper>
      <LoginColumnWrapper>
        <PrimaryButton
          id="login-btn"
          onClick={onClicklogin}
        >
          Log In
        </PrimaryButton>
        <DividerContainer>
          <Divider />
          <TextDivider>OR</TextDivider>
          <Divider />
        </DividerContainer>
        <MSButton onClick={onClickUnderwriterlogin}>
          <MicrosoftIcon />
          Log in with Microsoft
        </MSButton>
      </LoginColumnWrapper>
      {children}
    </GeneralLoginPage>
  );
}
export * from './GeneralLoginPage';
